import React, { FC, Fragment, useEffect, useState } from 'react'

import styled from 'styled-components'
import { Link } from 'react-router-dom'

import Arrow from 'src/Assets/Svg/common/chevronLeft.svg?react'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'

import { IconButton } from '../Buttons/IconButton'

const MOBILE_QUERY = `(max-width: ${MAX_MOBILE_WIDTH})`
const LAST_ITEM_COUNT = 1

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches,
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY)
    const handler = (e: MediaQueryListEvent): void => {
      setIsMobile(e.matches)
    }
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  return isMobile
}

const Separator = styled.div`
  width: 4px;
  height: 4px;
  background: ${({ theme }) => theme.color.darkGrey};
  border-radius: 50%;
  margin: 3px 8px 0;
  flex-shrink: 0;
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  max-width: 100%;

  border-radius: 40px;
  border: ${({ theme }) => `1px solid ${theme.color.lightGrey}`};
  background: ${({ theme }) => theme.color.white};
`

const StyledIconButton = styled(IconButton)`
  margin-left: 0px;
`

const LinksList = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;
  padding-right: 24px;
  align-items: center;
`

const LinkItem = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-style: normal;
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  flex-shrink: 0;
  flex-grow: 0;
  min-width: 0;
  overflow: hidden;
  cursor: pointer;

  flex-shrink: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 12px;

  &:hover {
    color: ${({ theme }) => theme.color.brandGreen} !important;
    background: none !important;
  }

  &:focus-visible {
    color: ${({ theme }) => theme.color.brandGreen} !important;
    outline: none !important;
  }

  &:active {
    color: ${({ theme }) => theme.color.mediumGrey} !important;
    background: none !important;
  }
`

const LastLink = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-style: normal;
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  flex-shrink: 0;
`

export type BreadCrumbItem = {
  path: string
  title: string
}

export interface BreadcrumbProps {
  items: Array<BreadCrumbItem>
  onButtonClick: () => void
}

export const Breadcrumb: FC<BreadcrumbProps> = ({ onButtonClick, items }) => {
  const isMobile = useIsMobile()
  const visibleItems = isMobile ? items.slice(-LAST_ITEM_COUNT) : items

  return (
    <Container>
      <StyledIconButton onClick={onButtonClick}>
        <Arrow />
      </StyledIconButton>

      <LinksList>
        {visibleItems.map((route, index) => {
          const isLast = index === visibleItems.length - 1
          return (
            <Fragment key={route.title}>
              {!isLast ? (
                <LinkItem to={route?.path ?? '/'}>{route.title}</LinkItem>
              ) : (
                <LastLink>{route.title}</LastLink>
              )}
              {!isLast && <Separator />}
            </Fragment>
          )
        })}
      </LinksList>
    </Container>
  )
}
